// Use FormInput on pages that use react-hook-form (useForm). It accepts `register` and `errors`
// from the form context and handles validation display automatically via getNestedError.
// If the page manages state manually (useState) without react-hook-form, use CustomInput instead.
import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';

interface FormInputProps {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'password' | 'tel' | 'number' | 'date';
  required?: boolean;
  placeholder?: string;
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  defaultValue?: string;
  disabled?: boolean;
  maskToNumeric?: boolean;
  maskToAlphanumeric?: boolean;
  maskToDecimal?: boolean;
  maxLength?: number;
  className?: string;
  /**
   * Optional element to render next to the error text (right side).
   * Use this for small contextual actions like a help icon button.
   */
  errorAction?: React.ReactNode;
  tooltip?: string;
  /** Line number badge rendered before the label (e.g. "1", "2a", "13b"). */
  labelPrefix?: string;
}

// Helper function to get nested error messages
const getNestedError = (errors: any, path: string) => {
  const keys = path.split('.');
  let current = errors;
  for (const key of keys) {
    if (current?.[key]) {
      current = current[key];
    } else {
      return undefined;
    }
  }
  return current;
};

const FormInput = ({
  name,
  label: labelText,
  type = 'text',
  required = false,
  register,
  errors,
  defaultValue,
  disabled = false,
  maskToNumeric = false,
  maskToAlphanumeric = false,
  maskToDecimal = false,
  maxLength,
  className,
  errorAction,
  tooltip,
  labelPrefix,
  ...props
}: FormInputProps) => {
  const safeId = name.trim().replace(/\s+/g, '_');
  const error = getNestedError(errors, name);

  const registered = register ? register(name) : null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (maskToNumeric) {
      value = value.replace(/\D/g, '');

      if (maxLength && value.length > maxLength) {
        value = value.slice(0, maxLength);
      }
    }

    if (maskToAlphanumeric) {
      value = value.replace(/[^a-zA-Z0-9 ]/g, '');
    }

    if (maskToDecimal) {
      // Allow digits and at most one decimal point
      value = value.replace(/[^\d.]/g, '');
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }
    }

    e.target.value = value;

    // Forward the (possibly masked) change to react-hook-form so its
    // internal state stays in sync with what the user actually sees.
    if (registered) {
      void registered.onChange(e);
    }
  };

  return (
    <div className="form-group">
      <div className="form-floating">
        <input
          id={safeId}
          type={type}
          {...(registered ?? {})}
          className={className ? `form-control ${className}` : 'form-control'}
          defaultValue={defaultValue}
          disabled={disabled}
          onChange={handleChange}
          maxLength={maxLength}
          {...props}
          placeholder=" "
        />
        <label
          htmlFor={safeId}
          className={labelPrefix ? 'flex items-center gap-1.5' : undefined}
        >
          {labelPrefix && (
            <span className="inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-on-surface-tertiary dark:text-on-surface-secondary flex-shrink-0">
              {labelPrefix}
            </span>
          )}
          {labelText}
          {required && (
            <span className="text-red-500 dark:text-red-400 ml-1">*</span>
          )}
          {tooltip && (
            <span className="ml-1 relative group cursor-help text-on-surface-quaternary">
              &#9432;
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-on-surface text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                {tooltip}
              </span>
            </span>
          )}
        </label>
      </div>

      {(error?.message || errorAction) && (
        <div className="inline-flex items-center gap-1">
          {error?.message && (
            <p role="alert" className="text-danger whitespace-nowrap">
              {error.message}
            </p>
          )}
          {errorAction && <div className="flex-shrink-0">{errorAction}</div>}
        </div>
      )}
    </div>
  );
};

export default FormInput;
